import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Grid,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material'
import { useTask } from 'hooks/useTask'
import { useIntersectLoadMore } from 'hooks/useIntersectLoadMore'
import TaskListSearchToolbar from 'components/TaskListSearchToolbar'
import { Task } from 'types/task'
import { productCodesFromTasks } from 'utils/taskSearchSuggestions'
import PullToRefresh from 'react-simple-pull-to-refresh'
import { useTaskContext } from 'contexts/task'
import { useTranslation } from 'react-i18next'
import { ERROR_CODE } from 'utils/errorCodes'

const WORKER_TASK_LIST_PAGE_SIZE = 50
const SCANNER_MEDIA = '@media (max-width: 360px) and (max-height: 520px)'
const TASK_PRIMARY_BUTTON_MIN_WIDTH = 56
const SCANNER_TASK_PRIMARY_BUTTON_MIN_WIDTH = 48
const TASK_REVOKE_BUTTON_MIN_WIDTH = 76
const SCANNER_TASK_REVOKE_BUTTON_MIN_WIDTH = 66

interface TaskListProps {
  setView: (view: 'tasks' | 'cart') => void
}

type SourceBinView = {
  bin?: { binCode?: string }
  quantity?: number
  note?: string | null
}

const dtf = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit'
})

const isRushTaskNote = (note: string | null | undefined) =>
  note === 'RUSH_TASK' || note === 'URGENT' || note === '加急'

const TaskList: React.FC<TaskListProps> = ({ setView }) => {
  const { t } = useTranslation()
  const {
    tasks,
    fetchTasks,
    loadMoreTasks,
    commitSearchKeyword,
    clearSearchKeyword,
    acceptTask,
    requestCancel,
    revokeCancelRequest,
    categoryCounts,
    categoryCountsReady,
    isLoading,
    isLoadingMore,
    hasMore,
    error
  } = useTask()
  const { fetchMyTask } = useTaskContext()

  const [loadingTasks, setLoadingTasks] = useState<Record<string, boolean>>({})
  const [cancelActionLoading, setCancelActionLoading] = useState<
    Record<string, boolean>
  >({})
  const [open, setOpen] = useState(false)
  const [showOutOfStock, setShowOutOfStock] = useState(false)
  const [category, setCategory] = useState<'assembly' | 'other'>('assembly')
  const [searchDraft, setSearchDraft] = useState('')
  const [searchToolbarExpanded, setSearchToolbarExpanded] = useState(false)
  const [showLoadMoreSpinner, setShowLoadMoreSpinner] = useState(false)
  const [confirmCancelTask, setConfirmCancelTask] = useState<Task | null>(null)

  const taskFilters = useMemo(
    () => ({
      category,
      stockMode: showOutOfStock ? 'outOfStock' : 'available'
    }) as const,
    [category, showOutOfStock]
  )

  const sourceDragRef = useRef<HTMLDivElement | null>(null)
  const isDraggingRef = useRef(false)
  const startXRef = useRef(0)
  const scrollLeftRef = useRef(0)

  useEffect(() => {
    if (error) setOpen(true)
  }, [error])

  useEffect(() => {
    void fetchTasks(taskFilters)
    setSearchToolbarExpanded(false)
  }, [fetchTasks, taskFilters])

  useEffect(() => {
    if (!isLoadingMore) {
      setShowLoadMoreSpinner(false)
      return
    }
    const id = window.setTimeout(() => setShowLoadMoreSpinner(true), 450)
    return () => window.clearTimeout(id)
  }, [isLoadingMore])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void fetchTasks(taskFilters)
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange, {
      passive: true
    } as any)
    return () => {
      document.removeEventListener(
        'visibilitychange',
        handleVisibilityChange as any
      )
    }
  }, [fetchTasks, taskFilters])

  const handleAccept = useCallback(
    async (taskID: string) => {
      setLoadingTasks(prev => ({ ...prev, [taskID]: true }))
      try {
        const success = await acceptTask(taskID)
        if (success) setView('cart')
        else setOpen(true)
      } finally {
        setLoadingTasks(prev => ({ ...prev, [taskID]: false }))
      }
    },
    [acceptTask, setView]
  )

  const runCancelAction = useCallback(
    async (taskID: string, action: 'request' | 'revoke') => {
      setCancelActionLoading(prev => ({ ...prev, [taskID]: true }))
      try {
        if (action === 'request') await requestCancel(taskID)
        else await revokeCancelRequest(taskID)
      } catch {
        setOpen(true)
      } finally {
        setCancelActionLoading(prev => ({ ...prev, [taskID]: false }))
      }
    },
    [requestCancel, revokeCancelRequest]
  )

  const handleManualRefresh = useCallback(async () => {
    await fetchTasks(taskFilters)
    await fetchMyTask()
  }, [fetchTasks, fetchMyTask, taskFilters])

  const filteredTasks = useMemo(
    () =>
      tasks.filter(task =>
        showOutOfStock
          ? !task.sourceBins || task.sourceBins.length === 0
          : task.sourceBins && task.sourceBins.length > 0
      ),
    [tasks, showOutOfStock]
  )

  const buildSourceBinsParts = (sourceBins?: SourceBinView[]) => {
    if (!sourceBins || sourceBins.length === 0) return []
    const seen = new Map<string, string | undefined>() // binCode -> note
    for (const it of sourceBins) {
      const code = it.bin?.binCode ?? '--'
      const rawNote = (it.note ?? '').trim()
      if (!seen.has(code)) {
        seen.set(code, rawNote.length > 0 ? rawNote : undefined)
      } else if (!seen.get(code) && rawNote.length > 0) {
        seen.set(code, rawNote)
      }
    }
    return Array.from(seen.entries()).map(([code, note]) => ({ code, note }))
  }

  const isAssemblyTask = (task: Task) => {
    const pickupCode =
      task.destinationBinCode || task.destinationBin?.binCode || ''
    return pickupCode.includes('_')
  }

  const visibleTasks = useMemo(() => {
    if (category === 'assembly') {
      return filteredTasks.filter(task => isAssemblyTask(task))
    }
    return filteredTasks.filter(task => !isAssemblyTask(task))
  }, [filteredTasks, category])

  const taskCounts = useMemo(() => {
    if (categoryCountsReady) {
      return showOutOfStock
        ? categoryCounts.outOfStock
        : categoryCounts.available
    }

    const assembly = filteredTasks.filter(task => isAssemblyTask(task)).length
    return {
      assembly,
      other: filteredTasks.length - assembly
    }
  }, [categoryCounts, categoryCountsReady, filteredTasks, showOutOfStock])

  /** 联想仅当前视图可见任务（总成/其他 + 缺货筛选）；搜索接口仍查全库 */
  const suggestionProductCodes = useMemo(
    () => productCodesFromTasks(visibleTasks),
    [visibleTasks]
  )

  const loadMoreEnabled =
    hasMore &&
    !isLoading &&
    !isLoadingMore &&
    tasks.length > 0 &&
    tasks.length >= WORKER_TASK_LIST_PAGE_SIZE

  const sentinelRef = useIntersectLoadMore(
    () => {
      void loadMoreTasks()
    },
    loadMoreEnabled,
    '0px',
    1200
  )

  return (
    <Box sx={{ backgroundColor: '#F7F9FC' }}>
      <PullToRefresh
        onRefresh={handleManualRefresh}
        pullingContent={
          <Box
            sx={{
              py: 0.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary'
            }}
          >
            <Typography variant='caption' sx={{ fontSize: 12, fontStyle: 'italic' }}>
              {t('taskList.pullToRefresh')}
            </Typography>
          </Box>
        }
        refreshingContent={
          <Box
            sx={{
              py: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              color: 'text.secondary'
            }}
          >
            <CircularProgress size={16} thickness={5} />
            <Typography variant='caption' sx={{ fontSize: 12 }}>
              {t('taskList.refreshing', 'Refreshing…')}
            </Typography>
          </Box>
        }
      >
        <Box
          p={1}
          pt={0.5}
          sx={{
            position: 'relative',
            backgroundColor: '#F7F9FC',
            pb: 'calc(env(safe-area-inset-bottom, 0px) + 8px)',
            [SCANNER_MEDIA]: {
              p: 0.5,
              pt: 0.25,
              pb: 'calc(env(safe-area-inset-bottom, 0px) + 4px)'
            }
          }}
        >
          <TaskListSearchToolbar
            expanded={searchToolbarExpanded}
            onExpandedChange={setSearchToolbarExpanded}
            value={searchDraft}
            onChange={setSearchDraft}
            onSubmit={q => {
              setSearchDraft(q)
              void commitSearchKeyword(q)
            }}
            onClear={() => {
              setSearchDraft('')
              void clearSearchKeyword()
            }}
            options={suggestionProductCodes}
            disabled={isLoading && tasks.length === 0}
            showOutOfStock={showOutOfStock}
            onToggleOutOfStock={() => setShowOutOfStock(v => !v)}
            centerSlot={
              <Box
                sx={{
                  display: 'flex',
                  gap: 0,
                  p: 0.2,
                  borderRadius: 999,
                  background: '#eef2f7',
                  flexShrink: 1,
                  minWidth: 0,
                  maxWidth: '100%'
                }}
              >
                <Button
                  variant={category === 'assembly' ? 'contained' : 'text'}
                  onClick={() => setCategory('assembly')}
                  sx={{
                    minWidth: 0,
                    flex: '1 1 50%',
                    height: 28,
                    borderRadius: 999,
                    textTransform: 'none',
                    fontSize: 11,
                    fontWeight: 800,
                    px: 0.75,
                    whiteSpace: 'nowrap',
                    [SCANNER_MEDIA]: {
                      height: 24,
                      fontSize: 10,
                      px: 0.55
                    }
                  }}
                >
                  {`${t('taskList.category.assembly')} (${taskCounts.assembly})`}
                </Button>
                <Button
                  variant={category === 'other' ? 'contained' : 'text'}
                  onClick={() => setCategory('other')}
                  sx={{
                    minWidth: 0,
                    flex: '1 1 50%',
                    height: 28,
                    borderRadius: 999,
                    textTransform: 'none',
                    fontSize: 11,
                    fontWeight: 800,
                    px: 0.75,
                    whiteSpace: 'nowrap',
                    [SCANNER_MEDIA]: {
                      height: 24,
                      fontSize: 10,
                      px: 0.55
                    }
                  }}
                >
                  {`${t('taskList.category.other')} (${taskCounts.other})`}
                </Button>
              </Box>
            }
          />

          {isLoading ? (
            <Box display='flex' justifyContent='center' mt={4}>
              <CircularProgress size={30} thickness={5} />
            </Box>
          ) : (
            <>
              {visibleTasks.length === 0 ? (
                <Typography color='text.secondary' textAlign='center'>
                  {t('taskList.empty')}
                </Typography>
              ) : (
                <Box>
                  {visibleTasks.map((task: Task) => {
                const isOutOfStock =
                  !task.sourceBins || task.sourceBins.length === 0
                const parts = buildSourceBinsParts(
                  task.sourceBins as unknown as SourceBinView[]
                )

                const firstBinCode = parts[0]?.code ?? ''
                const isAisleTask = firstBinCode.startsWith('AISLE-')
                const isRush = isRushTaskNote(task.note)
                const isCancelRequested = Boolean(
                  task.cancelRequestedAt && !task.cancelRequestDecision
                )

                const cardBorderColor = isCancelRequested
                  ? '#9ca3af'
                  : isRush
                    ? '#d32f2f'
                    : isAisleTask
                      ? '#059669'
                      : '#2563eb'
                const cardBgColor = isCancelRequested
                  ? '#f3f4f6'
                  : isRush
                    ? '#fff7f7'
                    : isAisleTask
                      ? '#ecfdf5'
                      : '#eff6ff'

                return (
                  <Card
                    key={task.taskID}
                    variant='outlined'
                    sx={{
                      position: 'relative',
                      mb: 2,
                      minHeight: 132,
                      borderRadius: 3,
                      backgroundColor: cardBgColor,
                      border: `1.5px solid ${cardBorderColor}`,
                      boxShadow: '0 2px 6px #0000000D',
                      [SCANNER_MEDIA]: {
                        mb: 1,
                        minHeight: 108,
                        borderRadius: 2,
                        borderWidth: 1.25
                      }
                    }}
                  >
                    {(isCancelRequested || !isOutOfStock) && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                          zIndex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          gap: 0.5,
                          maxWidth: '62%',
                          [SCANNER_MEDIA]: {
                            top: 5,
                            left: 6,
                            maxWidth: 86
                          }
                        }}
                      >
                        {isCancelRequested ? (
                          <Button
                            variant='contained'
                            onClick={() => void runCancelAction(task.taskID, 'revoke')}
                            disabled={cancelActionLoading[task.taskID]}
                            sx={{
                              fontSize: 10.5,
                              px: 1,
                              py: 0.5,
                              borderRadius: 2,
                              textTransform: 'uppercase',
                              fontWeight: 600,
                              minHeight: 30,
                              minWidth: TASK_REVOKE_BUTTON_MIN_WIDTH,
                              color: '#fff',
                              bgcolor: '#64748b',
                              whiteSpace: 'nowrap',
                              boxShadow: '0 3px 6px rgba(15, 23, 42, 0.28)',
                              '&:hover': {
                                bgcolor: '#475569',
                                boxShadow: '0 4px 8px rgba(15, 23, 42, 0.32)'
                              },
                              '&.Mui-disabled': {
                                bgcolor: '#94a3b8',
                                color: '#fff',
                                opacity: 0.65
                              },
                              [SCANNER_MEDIA]: {
                                fontSize: 9.2,
                                px: 0.4,
                                py: 0.25,
                                minHeight: 26,
                                minWidth: SCANNER_TASK_REVOKE_BUTTON_MIN_WIDTH,
                                borderRadius: 1.5
                              }
                            }}
                          >
                            {cancelActionLoading[task.taskID]
                              ? t('taskList.loading')
                              : t('taskList.revokeCancelRequest')}
                          </Button>
                        ) : (
                          <Button
                            variant='contained'
                            onClick={() => setConfirmCancelTask(task)}
                            disabled={cancelActionLoading[task.taskID]}
                            sx={{
                              fontSize: 11,
                              px: 0.7,
                              py: 0.5,
                              borderRadius: 2,
                              textTransform: 'uppercase',
                              fontWeight: 600,
                              minHeight: 30,
                              minWidth: TASK_PRIMARY_BUTTON_MIN_WIDTH,
                              color: '#fff',
                              bgcolor: '#f59e0b',
                              whiteSpace: 'nowrap',
                              boxShadow: '0 3px 6px rgba(180, 83, 9, 0.26)',
                              '&:hover': {
                                bgcolor: '#d97706',
                                boxShadow: '0 4px 8px rgba(180, 83, 9, 0.3)'
                              },
                              '&.Mui-disabled': {
                                bgcolor: '#fbbf24',
                                color: '#fff',
                                opacity: 0.65
                              },
                              [SCANNER_MEDIA]: {
                                fontSize: 9.5,
                                px: 0.35,
                                py: 0.2,
                                minHeight: 24,
                                minWidth: SCANNER_TASK_PRIMARY_BUTTON_MIN_WIDTH,
                                borderRadius: 1.4
                              }
                            }}
                          >
                            {cancelActionLoading[task.taskID]
                              ? t('taskList.loading')
                              : t('taskList.requestCancel')}
                          </Button>
                        )}
                      </Box>
                    )}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 0.5,
                        maxWidth: '64%',
                        flexWrap: 'wrap',
                        [SCANNER_MEDIA]: {
                          top: 6,
                          right: 6
                        }
                      }}
                    >
                      {isCancelRequested ? (
                        <Chip
                          label={t('taskList.cancelRequested')}
                          variant='outlined'
                          size='small'
                          sx={{
                            height: 22,
                            fontWeight: 700,
                            color: '#6b7280',
                            borderColor: '#9ca3af',
                            bgcolor: '#f9fafb',
                            '& .MuiChip-label': { px: 0.75 },
                            [SCANNER_MEDIA]: {
                              height: 18,
                              fontSize: 9,
                              maxWidth: 76,
                              '& .MuiChip-label': {
                                px: 0.45,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }
                            }
                          }}
                        />
                      ) : isRush ? (
                        <Chip
                          label={t('taskList.urgent')}
                          variant='outlined'
                          color='error'
                          size='small'
                          sx={{
                            fontWeight: 700,
                            height: 22,
                            '& .MuiChip-label': { px: 0.9 },
                            [SCANNER_MEDIA]: {
                              height: 18,
                              fontSize: 9,
                              maxWidth: 70,
                              '& .MuiChip-label': {
                                px: 0.45,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }
                            }
                          }}
                        />
                      ) : null}
                      </Box>
                    <CardContent
                      sx={{
                        py: 0.8,
                        px: 1.5,
                        pt: 2.8,
                        '&:last-child': { pb: 0.8 },
                        [SCANNER_MEDIA]: {
                          py: 0.45,
                          px: 1,
                          pt: 0.35,
                          '&:last-child': { pb: 0.45 }
                        }
                      }}
                    >
                      <Grid
                        container
                        spacing={0.75}
                        sx={{
                          [SCANNER_MEDIA]: {
                            '& > .MuiGrid-item': {
                              pt: 0.25
                            }
                          }
                        }}
                      >
                        <Grid item xs={12} textAlign='center'>
                          <Typography
                            variant='caption'
                            color='text.secondary'
                            fontSize={11}
                            sx={{
                              [SCANNER_MEDIA]: {
                                fontSize: 10,
                                lineHeight: 1.05
                              }
                            }}
                          >
                            {t('taskList.sourceBin')}
                          </Typography>

                          {isOutOfStock ? (
                            <Typography
                              fontSize={12}
                              fontWeight='medium'
                              sx={{
                                color: '#d32f2f',
                                [SCANNER_MEDIA]: {
                                  fontSize: 11,
                                  lineHeight: 1.1
                                }
                              }}
                            >
                              {t('taskList.outOfStock')}
                            </Typography>
                          ) : (
                            <Box
                              ref={sourceDragRef}
                              onMouseDown={e => {
                                isDraggingRef.current = true
                                startXRef.current = e.pageX
                                scrollLeftRef.current =
                                  e.currentTarget.scrollLeft
                              }}
                              onMouseMove={e => {
                                if (!isDraggingRef.current) return
                                const x = e.pageX - startXRef.current
                                e.currentTarget.scrollLeft =
                                  scrollLeftRef.current - x
                              }}
                              onMouseUp={() => {
                                isDraggingRef.current = false
                              }}
                              onMouseLeave={() => {
                                isDraggingRef.current = false
                              }}
                              onTouchStart={e => {
                                isDraggingRef.current = true
                                startXRef.current = e.touches[0].pageX
                                scrollLeftRef.current =
                                  e.currentTarget.scrollLeft
                              }}
                              onTouchMove={e => {
                                if (!isDraggingRef.current) return
                                const x = e.touches[0].pageX - startXRef.current
                                e.currentTarget.scrollLeft =
                                  scrollLeftRef.current - x
                              }}
                              onTouchEnd={() => {
                                isDraggingRef.current = false
                              }}
                              sx={{
                                whiteSpace: 'nowrap',
                                overflowX: 'auto',
                                overflowY: 'hidden',
                                fontSize: 13,
                                fontWeight: 'bold',
                                WebkitOverflowScrolling: 'touch',
                                userSelect: 'none',

                                scrollbarWidth: 'none',
                                msOverflowStyle: 'none',
                                '&::-webkit-scrollbar': {
                                  display: 'none'
                                },
                                [SCANNER_MEDIA]: {
                                  fontSize: 12,
                                  lineHeight: 1.15,
                                  maxWidth: '42%',
                                  mx: 'auto'
                                }
                              }}
                            >
                              {parts.map((p, idx) => (
                                <React.Fragment key={`${p.code}-${idx}`}>
                                  <span>{p.code}</span>
                                  {p.note && (
                                    <Typography
                                      component='span'
                                      fontSize={11}
                                      sx={{
                                        color: '#d32f2f',
                                        fontStyle: 'italic',
                                        ml: 0.3,
                                        [SCANNER_MEDIA]: {
                                          fontSize: 9.5
                                        }
                                      }}
                                    >
                                      ({p.note})
                                    </Typography>
                                  )}
                                  {idx < parts.length - 1 && (
                                    <span>{' / '}</span>
                                  )}
                                </React.Fragment>
                              ))}
                            </Box>
                          )}
                        </Grid>

                        <Grid item xs={4} textAlign='center'>
                          <Typography
                            variant='caption'
                            color='text.secondary'
                            fontSize={11}
                            sx={{
                              [SCANNER_MEDIA]: {
                                fontSize: 10,
                                lineHeight: 1.05
                              }
                            }}
                          >
                            {t('taskList.product')}
                          </Typography>
                          <Typography
                            fontWeight='bold'
                            fontSize={13}
                            sx={{
                              [SCANNER_MEDIA]: {
                                fontSize: 12,
                                lineHeight: 1.1
                              }
                            }}
                          >
                            {task.productCode}
                          </Typography>
                        </Grid>

                        <Grid item xs={4} textAlign='center'>
                          <Typography
                            variant='caption'
                            color='text.secondary'
                            fontSize={11}
                            sx={{
                              [SCANNER_MEDIA]: {
                                fontSize: 10,
                                lineHeight: 1.05
                              }
                            }}
                          >
                            {t('taskList.quantity')}
                          </Typography>
                          <Typography
                            fontWeight='bold'
                            fontSize={13}
                            sx={{
                              [SCANNER_MEDIA]: {
                                fontSize: 12,
                                lineHeight: 1.1
                              }
                            }}
                          >
                            {task.quantity || 'ALL'}
                          </Typography>
                        </Grid>

                        <Grid item xs={4} textAlign='center'>
                          <Typography
                            variant='caption'
                            color='text.secondary'
                            fontSize={11}
                            sx={{
                              [SCANNER_MEDIA]: {
                                fontSize: 10,
                                lineHeight: 1.05
                              }
                            }}
                          >
                            {t('taskList.targetBin')}
                          </Typography>
                          <Typography
                            fontWeight='bold'
                            fontSize={13}
                            sx={{
                              [SCANNER_MEDIA]: {
                                fontSize: 12,
                                lineHeight: 1.1
                              }
                            }}
                          >
                            {task.destinationBinCode || '--'}
                          </Typography>
                        </Grid>
                      </Grid>

                      <Divider sx={{ my: 0.75, [SCANNER_MEDIA]: { my: 0.45 } }} />

                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 1
                        }}
                      >
                        <Typography
                          variant='caption'
                          color='text.secondary'
                          fontSize={10.5}
                          sx={{
                            whiteSpace: 'pre-line',
                            minWidth: 0,
                            flex: '1 1 auto',
                            lineHeight: 1.35,
                            overflowWrap: 'anywhere',
                            [SCANNER_MEDIA]: {
                              fontSize: 9.5,
                              lineHeight: 1.18
                            }
                          }}
                        >
                          {`${t('taskList.createDate')}: ${dtf.format(new Date(task.createdAt))}\n${t(
                            'taskList.creator'
                          )}: ${task.creator?.firstName || '--'} ${task.creator?.lastName || ''}`}
                        </Typography>

                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            flexShrink: 0,
                            width: 'auto'
                          }}
                        >
                          {isCancelRequested ? (
                            <Button
                              variant='contained'
                              disabled
                              color={isAisleTask ? 'success' : 'primary'}
                              sx={{
                                fontSize: 11,
                                px: 2,
                                py: 0.5,
                                borderRadius: 2,
                                textTransform: 'uppercase',
                                fontWeight: 600,
                                minHeight: 30,
                                minWidth: TASK_PRIMARY_BUTTON_MIN_WIDTH,
                                opacity: 0.5,
                                whiteSpace: 'nowrap',
                                [SCANNER_MEDIA]: {
                                  fontSize: 10,
                                  px: 0.4,
                                  py: 0.25,
                                  minHeight: 26,
                                  minWidth: SCANNER_TASK_PRIMARY_BUTTON_MIN_WIDTH,
                                  borderRadius: 1.5
                                }
                              }}
                            >
                              {isAisleTask
                                ? t('taskList.takeOver')
                                : t('taskList.accept')}
                            </Button>
                          ) : (
                              <Button
                                variant='contained'
                                onClick={() => handleAccept(task.taskID)}
                                disabled={
                                  isOutOfStock || loadingTasks[task.taskID]
                                }
                                color={isAisleTask ? 'success' : 'primary'}
                                sx={{
                                  fontSize: 11,
                                  px: 2,
                                  py: 0.5,
                                  borderRadius: 2,
                                  textTransform: 'uppercase',
                                  fontWeight: 600,
                                  minHeight: 30,
                                  minWidth: TASK_PRIMARY_BUTTON_MIN_WIDTH,
                                  opacity: isOutOfStock ? 0.5 : 1,
                                  whiteSpace: 'nowrap',
                                  [SCANNER_MEDIA]: {
                                    fontSize: 10,
                                    px: 0.4,
                                    py: 0.25,
                                    minHeight: 26,
                                    minWidth: SCANNER_TASK_PRIMARY_BUTTON_MIN_WIDTH,
                                    borderRadius: 1.5
                                  }
                                }}
                              >
                                {loadingTasks[task.taskID]
                                  ? t('taskList.loading')
                                  : isAisleTask
                                    ? t('taskList.takeOver')
                                    : t('taskList.accept')}
                              </Button>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                )
                  })}
                </Box>
              )}
              {!isLoading && tasks.length > 0 && hasMore && (
                <Box
                  ref={sentinelRef}
                  sx={{ height: 24, flexShrink: 0 }}
                  aria-hidden
                />
              )}
              {showLoadMoreSpinner && (
                <Box display='flex' justifyContent='center' py={2}>
                  <CircularProgress size={22} thickness={5} />
                </Box>
              )}
            </>
          )}

          <Snackbar
            open={open}
            autoHideDuration={3000}
            onClose={() => setOpen(false)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert
              onClose={() => setOpen(false)}
              severity='error'
              variant='filled'
              sx={{ width: '100%' }}
            >
              {error
                ? t(ERROR_CODE[error] || 'taskList.error.unknown')
                : t('taskList.error.unknown')}
            </Alert>
          </Snackbar>
          <Dialog
            open={Boolean(confirmCancelTask)}
            onClose={() => setConfirmCancelTask(null)}
            maxWidth='xs'
            fullWidth
          >
            <DialogTitle sx={{ fontWeight: 800, pb: 0.5 }}>
              {t('taskList.confirmCancelRequestTitle')}
            </DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
              <Typography color='text.secondary' fontSize={14}>
                {t('taskList.confirmCancelRequestDescription', {
                  productCode: confirmCancelTask?.productCode || '--'
                })}
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
              <Button
                variant='text'
                color='inherit'
                onClick={() => setConfirmCancelTask(null)}
                sx={{ fontWeight: 700 }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant='contained'
                color='warning'
                disabled={
                  Boolean(confirmCancelTask?.taskID) &&
                  Boolean(cancelActionLoading[confirmCancelTask?.taskID || ''])
                }
                onClick={async () => {
                  if (!confirmCancelTask) return
                  await runCancelAction(confirmCancelTask.taskID, 'request')
                  setConfirmCancelTask(null)
                }}
                sx={{ fontWeight: 800 }}
              >
                {t('taskList.confirmCancelRequest')}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </PullToRefresh>
    </Box>
  )
}

export default TaskList
